import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager, terminate, clearIndexedDbPersistence } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

// Produção (checkin-app-aaca2) por padrão. Com REACT_APP_FB_ENV=staging (build/dev
// apontando para o projeto de teste porta-a-porta-teste). SEM a env, é SEMPRE produção.
const PROD_CONFIG = {
  apiKey: 'AIzaSyCjfRWDPFZ0QIS1GgJPbCXkt5HhHnAFuRo',
  authDomain: 'checkin-app-aaca2.firebaseapp.com',
  projectId: 'checkin-app-aaca2',
  storageBucket: 'checkin-app-aaca2.firebasestorage.app',
  messagingSenderId: '558212275195',
  appId: '1:558212275195:web:3b3b64d8913883230ac95e',
};
const STAGING_CONFIG = {
  apiKey: 'AIzaSyBsFQPIZ9xZC5lBC57YK33jCklxuhnrzFY',
  authDomain: 'porta-a-porta-teste.firebaseapp.com',
  projectId: 'porta-a-porta-teste',
  storageBucket: 'porta-a-porta-teste.firebasestorage.app',
  messagingSenderId: '910456939381',
  appId: '1:910456939381:web:795a4830bae1b5500d293c',
};
const isStaging = process.env.REACT_APP_FB_ENV === 'staging';
const firebaseConfig = isStaging ? STAGING_CONFIG : PROD_CONFIG;
if (isStaging) console.info('[Firebase] Ambiente: STAGING (porta-a-porta-teste)');

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
// Cache local PERSISTENTE (IndexedDB): guarda os docs já sincronizados entre reloads/sessões e entre
// abas. Com isso a aba Clientes (admin baixa ~15k docs) pinta instantânea do cache e só sincroniza o
// delta no fundo — antes, sem persistência, cada abertura rebaixava a coleção INTEIRA do servidor (~1 min).
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  // Rede corporativa (proxy/inspeção TLS) degrada o WebChannel streaming do Firestore — queries simples
  // ficam segundos "penduradas". O auto-detect cai pra long-polling quando o streaming não flui
  // (mesma flag que o app mobile já usa no RN).
  experimentalAutoDetectLongPolling: true,
});
export const functions = getFunctions(app, 'southamerica-east1');
export const storage = getStorage(app);

// LIMPEZA DO CACHE PERSISTENTE — obrigatória, não é higiene.
// O cache do IndexedDB guarda os ~15,7k clientes (CNPJ, endereço, GPS, potencial) no DISCO do
// navegador e SOBREVIVE ao logout. E leitura servida pelo cache NÃO passa pelas firestore.rules:
// num PC compartilhado de filial, o gerente abre Clientes, sai, o vendedor entra — e a base inteira
// segue legível pelo console/DevTools, sem nenhuma consulta chegar ao servidor e sem aparecer em
// monitoramento nenhum. As regras não protegem o que já está no disco.
//
// `clearIndexedDbPersistence` exige a instância TERMINADA e falha se houver outra aba usando o
// mesmo cache — por isso o reload no fim: com a instância morta, a página precisa renascer.
const CHAVE_ULTIMO_UID = 'ultimoUid';

export async function limparCachePersistente(): Promise<void> {
  try {
    await terminate(db);
    await clearIndexedDbPersistence(db);
  } catch (e) {
    // Outra aba aberta (failed-precondition) impede a limpeza. Não trava o logout — mas o cache
    // fica; por isso a checagem de troca de usuário no boot, abaixo, é a segunda linha de defesa.
    console.warn('Não foi possível limpar o cache local:', (e as any)?.code || e);
  }
}

// Segunda linha de defesa: se quem está entrando NÃO é o último usuário desta máquina, o cache é
// de outra pessoa — limpa antes de qualquer leitura. Cobre o caso do logout que não conseguiu
// limpar (outra aba) e o do navegador fechado no braço.
export async function garantirCacheDoUsuario(uid: string): Promise<void> {
  const anterior = localStorage.getItem(CHAVE_ULTIMO_UID);
  if (anterior && anterior !== uid) {
    await limparCachePersistente();
    localStorage.setItem(CHAVE_ULTIMO_UID, uid);
    window.location.reload();
    return;
  }
  localStorage.setItem(CHAVE_ULTIMO_UID, uid);
}

export function esquecerUltimoUid() { localStorage.removeItem(CHAVE_ULTIMO_UID); }

// (Havia aqui um `secondaryApp`/`secondaryAuth` — um segundo app Firebase inteiro, criado só para
// que o `createUserWithEmailAndPassword` do cadastro de vendedor não deslogasse o admin da sessão
// principal. Morreu quando a criação de conta foi para o servidor, na callable `criarUsuario`
// (functions/index.js), que usa o Admin SDK e não toca na sessão de ninguém. Não reintroduza: criar
// conta pelo CLIENTE exige o autocadastro ligado no Firebase Auth, e é justamente isso que a
// mudança para o servidor permitiu desligar.)

export default app;
